import type { CaptureResult, PostHog as ClientPostHog } from 'posthog-js';

import {
  RELEASE_PROPERTY,
  extractAccountSlug,
  getEnvironment,
  getReleaseVersion,
} from '../shared/posthog-config';

/**
 * This client is only ever constructed in a browser bundle, so its
 * client-application dimension is a constant. Mirrors
 * `SOURCE_APP.web` from `@kit/analytics/source-app` — not imported, because
 * `@kit/analytics` depends on this package and importing back would be circular.
 */
const SOURCE_APP_WEB = 'web';

interface ExceptionProperties {
  environment?: string;
  path?: string;
  account_slug?: string;
  $exception_message?: string;
  $exception_type?: string;
  $exception_values?: string[];
  $exception_types?: string[];
  $exception_list?: Array<{ value?: string; type?: string }>;
  [key: string]: unknown;
}

/**
 * Enriches autocaptured $exception events with properties the Slack webhook template expects.
 * PostHog autocapture uses $exception_types/$exception_values (arrays),
 * but our Slack template references $exception_type/$exception_message (flat strings).
 * Also adds environment, path, and account_slug which autocapture doesn't set.
 */
function enrichExceptionEvent(cr: CaptureResult | null): CaptureResult | null {
  if (!cr || cr.event !== '$exception') return cr;

  const props = cr.properties as ExceptionProperties;

  if (!props.environment) {
    props.environment = getEnvironment();
  }

  if (typeof window !== 'undefined') {
    if (!props.path) {
      props.path = window.location.pathname;
    }

    if (!props.account_slug) {
      props.account_slug = extractAccountSlug(
        window.location.hostname,
        window.location.pathname,
      );
    }
  }

  // Map autocapture's array format to the flat strings the Slack template expects
  if (!props.$exception_message && props.$exception_values) {
    props.$exception_message = props.$exception_values[0];
  }

  if (!props.$exception_type && props.$exception_types) {
    props.$exception_type = props.$exception_types[0];
  }

  // Also try extracting from $exception_list if available
  if (!props.$exception_message && Array.isArray(props.$exception_list)) {
    const first = props.$exception_list[0];

    if (first) {
      props.$exception_message = first.value;
      props.$exception_type = props.$exception_type || first.type;
    }
  }

  // Ensure $exception_list is always present (PostHog requires it)
  if (!Array.isArray(props.$exception_list)) {
    props.$exception_list = [
      {
        type: props.$exception_type,
        value: props.$exception_message,
      },
    ];
  }

  return cr;
}

/**
 * Thread-safe singleton for PostHog client instance
 * Prevents bundling server SDK in client builds
 */
export class PostHogClientSingleton {
  private static instance: ClientPostHog | null = null;
  private static initializationPromise: Promise<ClientPostHog> | null = null;
  private static initializationError: Error | null = null;

  /**
   * Get or initialize the PostHog client instance
   */
  static async getInstance(config: {
    key: string;
    host: string;
    ingestUrl?: string;
  }): Promise<ClientPostHog> {
    // Return existing instance if available
    if (this.instance) {
      return this.instance;
    }

    // If previous initialization failed, throw the error
    if (this.initializationError) {
      throw this.initializationError;
    }

    // If initialization is in progress, wait for it
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start new initialization
    this.initializationPromise = this.initialize(config);

    try {
      this.instance = await this.initializationPromise;

      return this.instance;
    } catch (error) {
      this.initializationError =
        error instanceof Error ? error : new Error(String(error));
      this.initializationPromise = null;

      throw this.initializationError;
    }
  }

  private static async initialize(config: {
    key: string;
    host: string;
    ingestUrl?: string;
  }): Promise<ClientPostHog> {
    const { posthog } = await import('posthog-js');

    // Only initialize if not already initialized globally and we're in a browser environment
    if (!posthog.__loaded && typeof window !== 'undefined') {
      posthog.init(config.key, {
        api_host: config.ingestUrl ?? config.host,
        ui_host: config.host,
        persistence: 'localStorage+cookie',
        person_profiles: 'always',
        capture_pageview: false,
        capture_pageleave: true,
        // The recorder is loaded asynchronously from PostHog. In Vitest that
        // request can outlive the Happy DOM window and reject inside unrelated
        // test files, making the full suite nondeterministic. Production keeps
        // the project-configured recording behavior unchanged.
        disable_session_recording: process.env.NODE_ENV === 'test',
        // Explicitly enable Web Vitals autocapture (LCP, FCP, CLS, INP) so
        // Core Web Vitals stream into PostHog as `$web_vitals` events. Turning
        // it on in code (rather than relying on the PostHog project UI toggle)
        // keeps the behavior consistent across every environment.
        capture_performance: { web_vitals: true },
        before_send: enrichExceptionEvent,
      });
    }

    // Register `account_slug` as a super property as early as possible so that
    // every event — including the first $pageview and any autocaptured event —
    // carries the tenant identifier. The canonical slug is re-registered from
    // the validated workspace context (AccountAnalyticsBridge); this URL-derived
    // value just closes the first-load race. No fallback: only register when a
    // slug actually resolves (null on personal/marketing/auth pages).
    if (typeof window !== 'undefined') {
      // `source_app` is unconditional: if this singleton is running, the event
      // came from the browser app. Registering it as a super property (rather
      // than at each call site) is what makes the dimension impossible to
      // forget — autocapture, $pageview, and every existing event pick it up
      // with no further changes. The desktop app registers `desktop` the same
      // way on its own client.
      posthog.register({ source_app: SOURCE_APP_WEB });

      // Stamp every event with the deploy release (commit SHA) so analytics and
      // errors can be filtered/broken-down by release — registered as a super
      // property for the same "impossible to forget" reason as `source_app`.
      // No fallback: only register when the SHA actually resolves (local dev /
      // preview without the var simply omits it — never a fake `'unknown'`).
      const release = getReleaseVersion();

      if (release) {
        posthog.register({ [RELEASE_PROPERTY]: release });
      }

      const slug = extractAccountSlug(
        window.location.hostname,
        window.location.pathname,
      );

      if (slug) {
        posthog.register({ account_slug: slug });
      }
    }

    return posthog;
  }

  /**
   * Reset for testing purposes
   */
  static reset(): void {
    this.instance = null;
    this.initializationPromise = null;
    this.initializationError = null;
  }
}
